// SfxTestScreen — /?sfx audition harness. Lists every sound in the
// manifest grouped by category with: a Play button, a "spam ×8"
// button (hear the variant rotation + pitch dither that prevents
// repeated actions sounding stamped), a variant-count chip, and a
// load/availability badge. Master volume + mute at the top.
//
// No auth, no game state — purely a generation/QA surface.

import { useEffect, useState, type ReactElement } from 'react'
import {
  SFX,
  playSfx,
  playSfxVariant,
  preloadAllSfx,
  isSfxAvailable,
  useSoundSettings,
  setAmbience,
  type SfxName,
  type SfxCategory,
  type AmbienceTrack,
} from '../sound'
import './SfxTestScreen.css'

const CATEGORY_LABELS: Record<SfxCategory, string> = {
  ui: 'UI feedback',
  shop: 'Shop',
  combine: 'Combine ceremony',
  battle: 'Battle',
  element: 'Element casts',
  meta: 'Meta / flow',
}

const CATEGORY_ORDER: SfxCategory[] = ['ui', 'shop', 'combine', 'battle', 'element', 'meta']

export function SfxTestScreen(): ReactElement {
  const { volume, muted, setVolume, toggleMuted } = useSoundSettings()
  // Re-render after the async Howl loads settle so availability badges
  // flip from "pending" to "ready".
  const [, force] = useState(0)
  useEffect(() => {
    preloadAllSfx()
    const t = setInterval(() => force((n) => n + 1), 400)
    const stop = setTimeout(() => clearInterval(t), 4000)
    return () => {
      clearInterval(t)
      clearTimeout(stop)
    }
  }, [])

  function spam(name: SfxName): void {
    // Fire 8 plays ~110ms apart so the throttle lets most through and
    // the variant rotation + dither is audible.
    let i = 0
    const id = setInterval(() => {
      playSfx(name, { ignoreThrottle: true })
      if (++i >= 8) clearInterval(id)
    }, 110)
  }

  const [bed, setBed] = useState<AmbienceTrack | null>(null)
  function toggleBed(track: AmbienceTrack): void {
    const next = bed === track ? null : track
    setBed(next)
    setAmbience(next)
  }
  // Stop the bed when leaving the page.
  useEffect(() => () => setAmbience(null), [])

  const names = Object.keys(SFX) as SfxName[]
  const generated = names.filter((n) => (SFX[n]?.variants ?? 0) > 0).length

  return (
    <div className="paper sfx-page">
      <header className="sfx-header">
        <div>
          <h1 className="cute-title sfx-title">音 · SFX Test Bench</h1>
          <p className="hand-note sfx-sub">
            {generated} / {names.length} sounds generated · variant rotation +
            pitch dither prevents repetition
          </p>
        </div>
        <div className="sfx-master">
          <button
            type="button"
            className={`cute-btn sfx-mute${muted ? ' sfx-mute--on' : ''}`}
            onClick={toggleMuted}
          >
            {muted ? '🔇 muted' : '🔊 on'}
          </button>
          <label className="sfx-vol">
            <span className="tiny-cap">volume</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
            />
            <span className="sfx-vol__num">{Math.round(volume * 100)}</span>
          </label>
        </div>
      </header>

      <section className="sfx-cat sfx-ambience">
        <h2 className="sfx-cat__title">Ambience loops</h2>
        <div className="sfx-ambience__row">
          {(['shop', 'battle'] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`cute-btn sfx-bed-btn${bed === t ? ' sfx-bed-btn--on' : ''}`}
              onClick={() => toggleBed(t)}
            >
              {bed === t ? '⏹ ' : '▶ '}
              {t} bed
            </button>
          ))}
          <span className="hand-note sfx-ambience__note">
            30s looping beds — crossfade on switch, quiet under sfx
          </span>
        </div>
      </section>

      {CATEGORY_ORDER.map((cat) => {
        const inCat = names.filter((n) => SFX[n]?.category === cat)
        if (inCat.length === 0) return null
        return (
          <section key={cat} className="sfx-cat">
            <h2 className="sfx-cat__title">{CATEGORY_LABELS[cat]}</h2>
            <div className="sfx-grid">
              {inCat.map((name) => {
                const def = SFX[name]
                if (!def) return null
                const pending = def.variants === 0
                const ready = !pending && isSfxAvailable(name)
                return (
                  <div
                    key={name}
                    className={`sfx-card${pending ? ' sfx-card--pending' : ''}`}
                  >
                    <div className="sfx-card__head">
                      <span className="sfx-card__label">{def.label}</span>
                      <span
                        className={`sfx-card__badge${
                          pending ? '' : ready ? ' sfx-card__badge--ready' : ' sfx-card__badge--err'
                        }`}
                      >
                        {pending ? 'pending' : ready ? 'ready' : 'load err'}
                      </span>
                    </div>
                    <code className="sfx-card__name">{name}</code>
                    <div className="sfx-card__meta">
                      <span className="chip">{def.variants || '—'} var</span>
                      <span className="chip">vol {Math.round((def.volume ?? 1) * 100)}</span>
                      <span className="chip">±{Math.round((def.pitch ?? 0.04) * 100)}%</span>
                      {def.throttleMs ? <span className="chip">{def.throttleMs}ms</span> : null}
                    </div>
                    <div className="sfx-card__actions">
                      <button
                        type="button"
                        className="cute-btn sfx-play"
                        disabled={pending}
                        onClick={() => playSfx(name, { ignoreThrottle: true })}
                        title="play with random variant + pitch dither (as in-game)"
                      >
                        ▶ play
                      </button>
                      <button
                        type="button"
                        className="cute-btn sfx-spam"
                        disabled={pending || def.variants < 2}
                        title={
                          def.variants < 2
                            ? 'needs ≥2 variants to hear rotation'
                            : 'fire 8× to hear variant rotation + dither'
                        }
                        onClick={() => spam(name)}
                      >
                        ⚡ ×8
                      </button>
                    </div>
                    {!pending && (
                      <div className="sfx-card__variants" role="group" aria-label="play individual variants">
                        <span className="sfx-card__variants-label tiny-cap">files</span>
                        {Array.from({ length: def.variants }, (_, i) => i + 1).map((v) => (
                          <button
                            key={v}
                            type="button"
                            className="sfx-variant-btn"
                            title={`play ${name}-${v}.mp3 raw (no dither) — judge this exact file`}
                            onClick={() => playSfxVariant(name, v)}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}
